import * as amplitude from '@amplitude/analytics-browser'
import Bugsnag from '@bugsnag/js'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useLocation, useNavigate } from 'react-router-dom'

// `api/demoAware` has the exact surface of `api` and passes straight through
// outside demo mode; in demo mode (SKE-4166) it serves the visitor's local
// snapshot and keeps mutations client-side. See src/demo/demoApi.ts.
import {
  fetchPosts,
  fetchSetting,
  postAction,
  postComment,
  fetchGridPreview,
  fetchPostSkedPreview,
  fetchComment,
} from 'api/demoAware'
import type { AccountSelectorProps } from 'Components'
import DemoDualView, { DEMO_TWO_PANE_MIN_WIDTH } from 'demo/DemoDualView'
import { trackDemoLanded } from 'demo/demoAnalytics'
import DemoConversionPanel from 'demo/DemoConversionPanel'
import DemoEmailGate from 'demo/DemoEmailGate'
import DemoHonestyBadge from 'demo/DemoHonestyBadge'
import DemoPayoff from 'demo/DemoPayoff'
import DemoResetButton from 'demo/DemoResetButton'
import DemoTour from 'demo/DemoTour'
import { useDemoAiReply } from 'demo/useDemoAiReply'
import { useDemoBrandedSetting } from 'demo/useDemoBrand'
import { isDemoMode } from 'demo/demoMode'
import type { ConversationTimelineItem } from 'Components/conversation'
import { getLastStatusChangeMessage } from 'Components/conversation/utils'
import type { PostOrGridView } from 'Components/PostPreviewSection'
import getReviewKeyFromURL from 'utils/getReviewKeyFromURL'
import { resolvePostPreviewVariant } from 'utils/postPreviewVariant'
import { reportError } from 'utils/reportError'

import type { ExternalReviewSettings } from './api/ExternalReviewSettings'
import type { UpcomingPosts } from './api/UpcomingPosts'
import App from './App'
import AppMobile from './AppMobile'
import type { StatusKey } from './normalizer'
import {
  normalizeSetting,
  normalizeAccounts,
  normalizePosts,
  normalizeStatusKey,
  normalizeSkedPreviewPost,
  normalizeMessages,
  normalizeRepliedMessage,
  normalizeGridPreview,
  isPostIsStory,
} from './normalizer'

/**
 * The external reviewer flow — the element behind the `review/*` route.
 *
 * It used to own the app's whole route table; that moved to `src/router.tsx`
 * so a second public surface (Content Collect's guest flow) could mount beside
 * it. Nothing about the reviewer behaviour changed: this component is still
 * mounted only under `review/*`, and the `/` invalid-link message it used to
 * render now lives in the router.
 */
const Homepage: React.FC = () => {
  // One source of truth for the mobile/desktop boundary. The real reviewer
  // path keeps its 906px split (App vs AppMobile) unchanged; in demo mode the
  // boundary moves to 1200 so the desktop two-pane only engages when it fits
  // (>= DEMO_TWO_PANE_MIN_WIDTH) and the mobile flip covers everything below.
  const mobileMaxWidth = isDemoMode() ? DEMO_TWO_PANE_MIN_WIDTH - 1 : 906
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth })

  const [isLoadingPost, setIsLoadingPost] = useState<boolean>(true)
  const [isLoadingComments, setIsLoadingComments] = useState<boolean>(false)

  const [posts, setPosts] = useState<any[]>([])

  const [skedPreviewPost, setSkedPreviewPost] = useState<any>([])

  const [accounts, setAccounts] = useState<AccountSelectorProps['accounts']>([])

  // sked preview need accounts to be built
  const [accountsRaw, setAccountsRaw] = useState<any>([])

  const [setting, setSetting] = useState<ExternalReviewSettings>({
    logoUrl: '',
    company: '',
    companyName: '',
    accounts: [],
    isConversationsEnabled: false,
    isReadOnly: false,
    collaborators: [],
    isStatusChangesVisible: true,
    isExternalNotesVisible: true,
    isPostActivityVisible: true,
    username: '',
    linkName: '',
  })
  const [statusKey, setStatusKey] = useState<StatusKey>({
    pending: '',
    approved: '',
    requestChange: '',
  })
  const [postRowId, setPostRowId] = useState('')

  const location = useLocation()
  const navigate = useNavigate()

  const [postId, setPostId] = useState('')
  const [lastAction, setLastAction] = useState<string | null>(null)

  const reviewKey = getReviewKeyFromURL(window.location.href)
  const hasReviewKey = Boolean(reviewKey)

  const [postActionLoading, setPostActionLoading] = useState<boolean>(false)
  const [showApprovalAnimation, setShowApprovalAnimation] =
    useState<boolean>(false)

  // Guard to ensure initial post selection only happens once
  const hasInitializedRef = useRef(false)
  const [messages, setMessages] = useState<
    | {
        postId: string
        messages?: ConversationTimelineItem[]
      }[]
    | undefined
  >([])
  const [isRepliedPosts, setIsRepliedPosts] = useState<string[]>([])
  const [isSubmittingReply, setIsSubmittingReply] = useState<boolean>(false)

  const [isGridPreviewAvailable, setIsGridPreviewAvailable] =
    useState<boolean>(false)

  const [postOrGridView, setpostOrGridView] = useState<PostOrGridView>('post')

  const [gridData, setGridData] = useState<any>(undefined)
  const previewVariant = resolvePostPreviewVariant(setting)

  /**
   * The approver header, re-skinned to the visitor's own client when the
   * approvals demo has been personalized (SKE-4166 phase 4). `setting` is
   * loaded once into state on mount, so the brand has to be overlaid at render
   * time to reach the header at all. Outside demo mode this returns the very
   * same object, so the real reviewer path is untouched.
   */
  const brandedSetting = useDemoBrandedSetting(setting)

  const [inactiveError, setInactiveError] = useState<string>('')
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    if (lastAction) {
      setLastAction(null)
    }
  }, [postId, lastAction])

  /**
   * Top of the demo funnel (SKE-4166 phase 7). Fired from a mount effect rather
   * than from `main.tsx` so it lands after Amplitude has been initialised, and
   * guarded once inside `trackDemoLanded` so a re-render can never inflate the
   * denominator. No-op on the real reviewer path.
   */
  useEffect(() => {
    if (!isDemoMode()) {
      return
    }
    trackDemoLanded({ is_mobile: isMobile })
    // Once per page load — the viewport at mount is the visit's device, and a
    // later resize is not a second landing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!hasReviewKey) {
      setIsLoadingPost(false)
      return
    }

    const doWork = async (reviewKey: string) => {
      try {
        const setting = await fetchSetting(reviewKey)
        const normalizedSetting = normalizeSetting(setting)

        const posts = await fetchPosts(reviewKey)

        if (!posts) {
          throw new Error('Failed to fetch posts')
        }

        setSetting(normalizedSetting)

        setAccountsRaw(setting.accounts)
        setAccounts(normalizeAccounts(setting))

        setPosts(normalizePosts(posts, normalizedSetting.isReadOnly))
        setStatusKey(normalizeStatusKey(setting))

        setIsLoadingPost(false)

        if (setting.adminId && setting.company) {
          const groupName = setting.company
          const grpIdentify = new amplitude.Identify().set(
            'AdminID',
            setting.adminId
          )
          amplitude.setGroup('Org', groupName)
          amplitude.groupIdentify('Org', groupName, grpIdentify)
        }
        if (setting.plan) {
          const identifyContext = new amplitude.Identify()
          identifyContext.set('plan', setting.plan)
          amplitude.identify(identifyContext)
        }
      } catch (error: unknown) {
        reportError(error)
        if (error instanceof Error && error.message) {
          setInactiveError(`Error loading: ${error.message}`)
          return false
        }
        setInactiveError('This review link is inactive')
        return false
      }
    }
    doWork(reviewKey).catch((error: unknown) => {
      reportError(error)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasReviewKey, reviewKey])

  const savePostRowId = (posts: UpcomingPosts[], postId: string) => {
    for (const { posts: _posts, id } of posts) {
      const found = _posts.some((post) => post.id === postId)

      if (found) {
        setPostRowId(id)
        break
      }
    }
  }

  const selectPostByPostId = (posts: UpcomingPosts[], postId: string) => {
    return posts.map((item) => ({
      ...item,
      posts: item.posts.map((post) =>
        post.id === postId
          ? {
              ...post,
              isSelected: true,
            }
          : post
      ),
    }))
  }

  const navigateToSelectedPost = (nextPostId: string) => {
    const nextPath = `/review/${reviewKey}/posts/${nextPostId}`

    if (location.pathname === nextPath) {
      return
    }

    navigate(nextPath, {
      replace: true,
    })
  }

  useEffect(() => {
    if (!isLoadingPost && !hasInitializedRef.current) {
      hasInitializedRef.current = true

      // user come from sked app link without postId
      let postId = location.pathname.includes('posts')
        ? location.pathname.split('/').pop()
        : undefined

      if (!postId || isNaN(+postId) === false) {
        //select first post if there is a pending post
        postId = posts
          .flatMap((item) => item.posts)
          .find((item) => item.status === 'pending')?.id

        if (postId) {
          const firstPostInFirstElement = document.querySelector(
            `[data-postid="${postId}"]`
          ) as HTMLDivElement
          firstPostInFirstElement?.click()
        } else {
          const firstPostInFirstElement = document.querySelector(
            `[data-postindex="1"]`
          ) as HTMLDivElement
          firstPostInFirstElement?.click()
        }
      } else {
        // user come from sked app link with postId when a post is selected
        const nextPosts = selectPostByPostId(posts, postId)

        setPosts(nextPosts)
        setPostId(postId)
        savePostRowId(nextPosts, postId)

        const firstPostInFirstRowIsSelected = nextPosts.map((item) =>
          item.posts.find((_, i) => i === 0)
        )[0]?.isSelected

        if (!firstPostInFirstRowIsSelected) {
          const toBeSelectedPostElem = document.querySelector(
            `[data-postid="${postId}"`
          ) as HTMLDivElement

          // delay because posts take time to render before scroll
          const postDelay = setTimeout(() => {
            toBeSelectedPostElem?.scrollIntoView({
              behavior: 'smooth',
            })
          }, 300)
          return () => clearTimeout(postDelay)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingPost])

  const handleAccountChange = (selelectedOptions: string[]) => {
    const nextPosts = posts
      .map((item) => ({
        ...item,
        posts: item.posts.map((post: { accounts: { name: string }[] }) => ({
          ...post,
          isVisible: post.accounts.some((account: { name: string }) =>
            selelectedOptions.includes(account.name)
          ),
        })),
      }))
      .map((item) => ({
        ...item,
        isVisible: item.posts.some(
          (post: { isVisible: any }) => post.isVisible
        ),
      }))

    setPosts(nextPosts)
  }

  const handleSetPosts = (
    postRowId: string,
    postId: string,
    status: string,
    statusLabel?: string
  ) => {
    const nextPosts = posts.map((item) =>
      item.id === postRowId
        ? {
            ...item,
            posts: item.posts.map(
              (post: { id: string; statusLabel?: string }) =>
                post.id === postId
                  ? {
                      ...post,
                      status,
                      statusLabel,
                      isUndo: status !== 'pending',
                    }
                  : post
            ),
          }
        : item
    )

    // For non-approved states, refresh the preview for the selected post
    if (status !== 'approved') {
      fetchPostSkedPreviewAsync(reviewKey, postId)
    }

    setPosts(nextPosts)
  }

  const handleSetPostsMobile = (
    postId: string,
    status: string,
    statusLabel?: string
  ) => {
    const nextPosts = posts.map((item) => ({
      ...item,
      posts: item.posts.map((post: { id: string; statusLabel?: string }) =>
        post.id === postId
          ? {
              ...post,
              status,
              statusLabel,
              isUndo: status !== 'pending',
            }
          : post
      ),
    }))

    setPosts(nextPosts)
  }

  // select post
  const handleCardClick = (postRowId: string, clickedPostId: string) => {
    // Skip if already viewing this post to prevent redundant history.replaceState() calls
    if (postId === clickedPostId) {
      return
    }

    const nextPosts = posts.map((item) => ({
      ...item,
      posts:
        item.id === postRowId
          ? item.posts.map((post: { id: string }) => ({
              ...post,
              isSelected: post.id === clickedPostId,
            }))
          : item.posts.map((post: any) => ({ ...post, isSelected: false })),
    }))
    setPosts(nextPosts)
    setPostId(clickedPostId)
    savePostRowId(nextPosts, clickedPostId)

    navigateToSelectedPost(clickedPostId)
  }

  const handleNavigatePost = (postIndex: number) => {
    //find postId by postIndex
    const postId = posts
      .flatMap((o) => o.posts)
      .find((post) => post.postIndex === postIndex)?.id

    if (postId) {
      if (postId === location.pathname.split('/').pop()) {
        return
      }

      // select post
      const nextPosts = posts.map((item) => ({
        ...item,
        posts: item.posts.map((post: { id: any }) => ({
          ...post,
          isSelected: post.id === postId,
        })),
      }))

      setPosts(nextPosts)
      setPostId(postId)
      savePostRowId(nextPosts, postId)

      navigateToSelectedPost(postId)

      const toBeSelectedPostElem = document.querySelector(
        `[data-postid="${postId}"`
      ) as HTMLDivElement

      toBeSelectedPostElem?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }

  const fetchCommentAsync = async (reviewKey: string, postId: string) => {
    setIsLoadingComments(true)

    try {
      const messagesResponse = await fetchComment(reviewKey, postId)
      const nextPostMessages = {
        postId,
        messages: messagesResponse
          ? normalizeMessages(messagesResponse) || []
          : [],
      }

      setMessages((currentMessages) => {
        const existingMessages = currentMessages || []
        const hasExistingPost = existingMessages.some(
          (item) => item.postId === postId
        )

        if (!hasExistingPost) {
          return [...existingMessages, nextPostMessages]
        }

        return existingMessages.map((item) =>
          item.postId === postId ? nextPostMessages : item
        )
      })
      return [nextPostMessages]
    } catch (error) {
      reportError(error)
      // Return empty messages on failure so UI doesn't break
      return [{ postId, messages: [] }]
    } finally {
      setIsLoadingComments(false)
    }
  }

  /**
   * The approvals demo's marketer replies to the visitor's notes (SKE-4166
   * phase 5). It writes into the visitor's own local snapshot and then needs
   * this component to re-read the thread, so it takes `fetchCommentAsync`.
   * Outside demo mode it registers nothing and never calls back, so the real
   * reviewer path is untouched.
   */
  useDemoAiReply(reviewKey, fetchCommentAsync)

  const fetchPostSkedPreviewAsync = async (
    reviewKey: string,
    postId: string
  ) => {
    const skedPreviewResponse = await fetchPostSkedPreview(reviewKey, postId)

    if (!skedPreviewResponse) {
      setSkedPreviewPost(null)
      return
    }

    const normalizedSkedPreviewPost = normalizeSkedPreviewPost(
      skedPreviewResponse,
      accountsRaw
    )

    setSkedPreviewPost(normalizedSkedPreviewPost)

    if (
      skedPreviewResponse &&
      skedPreviewResponse.platform === 'IG' &&
      skedPreviewResponse.post_stage !== 'draft' &&
      !isPostIsStory(skedPreviewResponse)
    ) {
      const gridPreviewResponse = await fetchGridPreview(reviewKey, postId)

      if (gridPreviewResponse) {
        const normalizedGridPreviewPost = normalizeGridPreview(
          gridPreviewResponse,
          postId
        )
        setIsGridPreviewAvailable(true)
        setGridData(normalizedGridPreviewPost)
      }
    } else {
      setpostOrGridView('post')
      setIsGridPreviewAvailable(false)
    }
  }

  // postId changed
  useEffect(() => {
    if (posts.length && reviewKey && !isLoadingPost && postId && !isMobile) {
      if (posts.length) {
        // fetchCommentAsync handles its own errors internally
        fetchCommentAsync(reviewKey, postId)
        fetchPostSkedPreviewAsync(reviewKey, postId).catch((error: unknown) => {
          reportError(error)
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length, isLoadingPost, reviewKey, postId, isMobile])

  const scrollToForm = () => {
    const textareaElem = document.querySelector(
      `[data-textarea-postid="${postId}"]`
    )

    // scroll to bottom 4%
    const toScrollPosition = window.innerHeight / 4
    textareaElem?.scrollIntoView(false)
    window.scrollBy(0, +toScrollPosition)
  }

  const scrollToPostInList = (postId: string) => {
    // Use setTimeout to ensure the DOM has updated after navigation
    setTimeout(() => {
      const postElement = document.querySelector(`[data-postid="${postId}"]`)
      const postListColumn = document.getElementById('post-list-column')

      if (postElement && postListColumn) {
        // Get the positions
        const postRect = postElement.getBoundingClientRect()
        const columnRect = postListColumn.getBoundingClientRect()

        // Calculate the scroll position to center the post in the viewport
        const scrollTop =
          postListColumn.scrollTop +
          postRect.top -
          columnRect.top -
          columnRect.height / 2 +
          postRect.height / 2

        // Smooth scroll to the post
        postListColumn.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        })
      }
    }, 100)
  }

  const navigateToNextPost = () => {
    const allPosts = posts.flatMap((item) => item.posts)
    const currentIndex = allPosts.findIndex((post) => post.id === postId)

    // Find the next pending post after the current one
    const nextPendingPost = allPosts
      .slice(currentIndex + 1)
      .find((post) => post.status === 'pending')

    if (nextPendingPost) {
      // Navigate to the next pending post after current
      const postRow = posts.find((item) =>
        item.posts.some((p: any) => p.id === nextPendingPost.id)
      )
      if (postRow) {
        handleCardClick(postRow.id, nextPendingPost.id)
        scrollToPostInList(nextPendingPost.id)
      }
    } else {
      // If no pending post after current, loop around to find the first pending post
      const firstPendingPost = allPosts.find(
        (post) => post.status === 'pending'
      )
      if (firstPendingPost) {
        const postRow = posts.find((item) =>
          item.posts.some((p: any) => p.id === firstPendingPost.id)
        )
        if (postRow) {
          handleCardClick(postRow.id, firstPendingPost.id)
          scrollToPostInList(firstPendingPost.id)
        }
      }
    }
  }

  const handleApprovalAnimationComplete = () => {
    setShowApprovalAnimation(false)
    setLastAction('approved')
    navigateToNextPost()
  }

  const handlePostAction = async (postId: string, status: string) => {
    setPostActionLoading(true)
    setActionError(null) // Clear any previous error
    let statusLabel: string | undefined

    try {
      if (status === 'approved') {
        const result = await postAction(
          reviewKey,
          postId,
          statusKey['approved']
        )
        statusLabel = result?.post?.status?.label
        handleSetPosts(postRowId, postId, status, statusLabel)
        await fetchCommentAsync(reviewKey, postId)

        // Show approval animation
        setShowApprovalAnimation(true)
        setPostActionLoading(false)
        // Don't set lastAction yet, will be set after animation completes
        return
      } else if (status === 'requestChange') {
        const result = await postAction(
          reviewKey,
          postId,
          statusKey['requestChange']
        )
        statusLabel = result?.post?.status?.label
        handleSetPosts(postRowId, postId, status, statusLabel)
        await fetchCommentAsync(reviewKey, postId)
        if (isMobile) {
          scrollToForm()
        }
      } //for undo
      else if (status === 'pending') {
        const result = await postAction(reviewKey, postId, statusKey['pending'])
        statusLabel = result?.post?.status?.label
        setIsRepliedPosts((repliedPostIds) =>
          repliedPostIds.filter((repliedPostId) => repliedPostId !== postId)
        )

        // Hide approval animation if it's showing
        setShowApprovalAnimation(false)

        // undo is clicked when the user has not selected a post, let's call it specialUndo
        const postIdInURL = location.pathname.split('/')[4]
        const specialUndo = postIdInURL !== postId
        if (!specialUndo) {
          handleSetPosts(postRowId, postId, status, statusLabel)
          await fetchCommentAsync(reviewKey, postId)
        } else {
          const nextPosts = posts.map((item) => ({
            ...item,
            posts: item.posts.map(
              (post: { id: string; statusLabel?: string }) =>
                post.id === postId
                  ? {
                      ...post,
                      status,
                      statusLabel,
                      isSelected: true,
                      isUndo: false,
                    }
                  : { ...post, isSelected: false }
            ),
          }))

          setPosts(nextPosts)
          setTimeout(() => {
            const element = document.querySelector(
              `[data-postid="${postId}"`
            ) as HTMLDivElement
            if (element) {
              element.click()
            }
          }, 300)
        }
      }

      setLastAction(status)

      if (isMobile) {
        handleSetPostsMobile(postId, status, statusLabel)
      }
    } catch (error) {
      reportError(error)
      setActionError('Action failed. Please try again.')
      // Clear error after 5 seconds
      setTimeout(() => setActionError(null), 5000)
    } finally {
      setPostActionLoading(false)
    }
  }

  const handleSubmitReply = async (
    text: string,
    postId: string,
    commentId: string
  ) => {
    setIsSubmittingReply(true)

    // Track analytics for reply submission
    amplitude.track('ext-rejection-message-sent')

    try {
      const messagesResponse = await postComment(
        reviewKey,
        postId,
        text,
        commentId
      )

      if (!messagesResponse) {
        Bugsnag.notify(
          new Error(
            'Post comment failed so not continuing handling submit reply'
          ),
          function (event) {
            event.addMetadata('default', {
              postId,
              commentId,
              messagesResponse,
            })
          }
        )
        return
      }
      const message = normalizeRepliedMessage(messagesResponse)
      const nextMessage = messages?.map((item) =>
        item.postId === postId
          ? {
              ...item,
              messages: [...(item.messages || []), message],
            }
          : item
      )
      setMessages(nextMessage)
      setIsRepliedPosts([...isRepliedPosts, postId])
      setLastAction('submitChangeDetail')
    } catch (error) {
      reportError(error)
    } finally {
      setIsSubmittingReply(false)
    }
  }

  // sked-preview related states
  const totalPostLength = posts.flatMap((item) => item.posts).length
  const currentPostIndex =
    posts.flatMap((item) => item.posts).find((post) => post.isSelected)
      ?.postIndex ?? 0

  // conversations related stats
  const currentPostStatus =
    posts
      .find((item) => item.id === postRowId)
      ?.posts.find((post: { id: string }) => post.id === postId)?.status ??
    'pending'

  const postMessages =
    currentPostStatus === 'requestChange'
      ? messages?.find((item: { postId: string }) => item.postId === postId)
      : undefined

  const lastStatusChangeMessage = postMessages?.messages
    ? getLastStatusChangeMessage(postMessages.messages)
    : null

  const lastStatusChangeComment = lastStatusChangeMessage?.comment

  const hasPostedComment = Boolean(
    lastStatusChangeMessage?.isMessageOwner &&
    lastStatusChangeComment &&
    lastStatusChangeComment.trim().length > 0
  )

  const lastMessageId = lastStatusChangeMessage?.id

  const updatePostState = useCallback(
    (_reviewKey: string, postId: string, postStatus: string) => {
      if (!postActionLoading) {
        // Track analytics inside the guard so they only fire when action proceeds
        if (postStatus === 'pending') {
          amplitude.track('ext-undo')
        } else if (postStatus === 'approved') {
          amplitude.track('ext-approved-click')
        } else if (postStatus === 'requestChange') {
          amplitude.track('ext-rejection-click')
        }

        handlePostAction(postId, postStatus).catch((error: unknown) => {
          reportError(error)
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postActionLoading, handlePostAction, isMobile]
  )

  if (inactiveError) {
    return (
      <p
        style={{
          margin: '10px',
          fontSize: '14px',
        }}
      >
        {inactiveError}
      </p>
    )
  }

  return (
    <>
      {/* Honesty framing (SKE-4166 phase 7). On the desktop two-pane the
          disclosure lives in the client browser chrome (ClientFrame), so the
          floating badge is only for the demo's narrow flip/role-toggle layouts.
          (DemoHonestyBadge self-guards to null outside demo mode, so the
          `!isDemoMode()` half of the old condition was dead.) */}
      {isDemoMode() && isMobile && <DemoHonestyBadge />}
      {isDemoMode() && <DemoResetButton />}
      {/* 2-step intro tour (SKE-4234). Desktop two-pane only — it teaches the
          two panes, which the mobile flip presents differently. Auto-once. */}
      {isDemoMode() && !isMobile && <DemoTour />}
      {/* Email soft gate (SKE-4166 phase 3). Mounted here — a single place,
          inside the demo guard — so it is shared by the mobile and desktop
          branches below and can never appear twice. */}
      {isDemoMode() && <DemoEmailGate />}
      {/* End-state CTAs (SKE-4166 phase 6). Mounted alongside the gate, for the
          same reason: one place inside the demo guard, shared by the mobile and
          desktop branches below, so it can never appear twice. */}
      {isDemoMode() && <DemoConversionPanel />}
      {/* Queue-cleared payoff (SKE-4234): a restrained, client-pane-scoped
          confetti burst at 100% reviewed. The progress-bar flourish
          (MarketerMirror) and the auto-opening 3-door modal (DemoConversionPanel,
          off the same cleared state) complete the beat. Self-scopes to the
          client side, so it's safe on both the desktop two-pane and mobile. */}
      {isDemoMode() && <DemoPayoff />}
      {isMobile ? (
        // `DemoDualView` renders its children and nothing else outside
        // demo mode, so the real reviewer path is unchanged. In demo mode
        // it adds the marketer's "what your team sees" side (SKE-4166).
        <DemoDualView isMobile>
          <div>
            <AppMobile
              isLoading={isLoadingPost}
              setting={{
                logoUrl: brandedSetting.logoUrl,
                companyName: brandedSetting.companyName,
              }}
              accountSelectorProps={{
                accounts,
                onChange: handleAccountChange,
              }}
              upcomingPosts={posts}
              accountsRaw={accountsRaw}
              updatePostState={updatePostState}
              conversationProps={{
                isLoading: isLoadingComments,
                messages,
                isRepliedPosts,
                postId,
                reviewKey, // mobile
                fetchComment: fetchCommentAsync, // mobile
                isConversationsEnabled: setting.isConversationsEnabled,
                collaborators: setting.collaborators || [],
                isReadOnly: setting.isReadOnly,
                isStatusChangesVisible: setting.isStatusChangesVisible,
                isExternalNotesVisible: setting.isExternalNotesVisible,
                isPostActivityVisible: setting.isPostActivityVisible,
                username: setting.username,
                timezone: setting.timezone ?? undefined,
                isReplied: hasPostedComment || isRepliedPosts.includes(postId),
              }}
              postActionProps={{
                isLoading: postActionLoading,
                postStatus: currentPostStatus,
                postId,
                reviewKey,
                updatePostState,
                // Reply functionality props
                canReply: currentPostStatus === 'requestChange',
                isReplied: hasPostedComment || isRepliedPosts.includes(postId),
                isSubmitting: isSubmittingReply,
                isConversationsEnabled: setting.isConversationsEnabled,
                onReplySubmit: handleSubmitReply,
                lastMessageId: lastMessageId,
                collaborators: setting.collaborators || [],
                isMobile: true,
                // Approval animation props
                showApprovalAnimation,
                onApprovalAnimationComplete: handleApprovalAnimationComplete,
                // Error state
                actionError,
              }}
              reviewKey={reviewKey}
              postId={postId}
              isReadOnly={setting.isReadOnly}
              previewVariant={previewVariant}
            />
          </div>
        </DemoDualView>
      ) : (
        <DemoDualView>
          <App
            lastAction={lastAction}
            isLoading={isLoadingPost}
            setting={{
              logoUrl: brandedSetting.logoUrl,
              companyName: brandedSetting.companyName,
            }}
            accountSelectorProps={{
              accounts,
              onChange: handleAccountChange,
            }}
            upcomingPosts={posts}
            handleCardClick={handleCardClick}
            navigatePost={handleNavigatePost}
            postPreviewSectionProps={{
              currentPostIndex,
              totalPostLength,
              navigatePost: handleNavigatePost,
              skedPreviewPost,
              postOrGridView,
              setpostOrGridView,
              isGridPreviewAvailable,
              gridData,
              previewVariant,
            }}
            conversationProps={{
              status: currentPostStatus,
              isLoading: isLoadingComments,
              messages,
              isReplied: hasPostedComment || isRepliedPosts.includes(postId),
              postId,
              isConversationsEnabled: setting.isConversationsEnabled,
              collaborators: setting.collaborators || [],
              isReadOnly: setting.isReadOnly,
              isStatusChangesVisible: setting.isStatusChangesVisible,
              isExternalNotesVisible: setting.isExternalNotesVisible,
              isPostActivityVisible: setting.isPostActivityVisible,
              username: setting.username,
              timezone: setting.timezone ?? undefined,
            }}
            updatePostState={updatePostState}
            postActionProps={{
              isLoading: postActionLoading,
              postStatus: currentPostStatus,
              postId,
              reviewKey,
              updatePostState,
              // Reply functionality props
              canReply: currentPostStatus === 'requestChange',
              isReplied: hasPostedComment || isRepliedPosts.includes(postId),
              isSubmitting: isSubmittingReply,
              isConversationsEnabled: setting.isConversationsEnabled,
              onReplySubmit: handleSubmitReply,
              lastMessageId: lastMessageId,
              collaborators: setting.collaborators || [],
              isMobile: false,
              // Approval animation props
              showApprovalAnimation,
              onApprovalAnimationComplete: handleApprovalAnimationComplete,
              // Error state
              actionError,
            }}
            isReadOnly={setting.isReadOnly}
            previewVariant={previewVariant}
            // Demo desktop two-pane only (isDemoMode() is false for real
            // reviewers, and this branch is already >= 1200 in demo). The
            // compact grid + discussion drawer fit the client area beside
            // the "Your Sked" pane.
            demoCompact={isDemoMode()}
          />
        </DemoDualView>
      )}
    </>
  )
}

export default Homepage
